import { RenderMode } from './types';

export class Renderer {
    private canvas: HTMLCanvasElement;
    private device!: GPUDevice;
    private context!: GPUCanvasContext;
    private presentationFormat!: GPUTextureFormat;
    private pipelines = new Map<string, GPURenderPipeline | GPUComputePipeline>();
    private bindGroups = new Map<string, GPUBindGroup>();
    private sampler!: GPUSampler;
    private nonFilteringSampler!: GPUSampler;
    private imageUrls: string[] = [];
    private v2ComputeUniformBuffer!: GPUBuffer;
    private imageTexture!: GPUTexture;
    private writeTexture!: GPUTexture;
    private imageDimensions = { width: 1, height: 1 }; // Add property to store dimensions

    private staticDepthTexture!: GPUTexture;

    constructor(canvas: HTMLCanvasElement) { this.canvas = canvas; }

    public async init(): Promise<boolean> {
        if (!navigator.gpu) return false;
        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter) return false;

        // --- FIXED: Restore the feature request logic ---
        const requiredFeatures: GPUFeatureName[] = [];
        if (adapter.features.has('float32-filterable')) {
            requiredFeatures.push('float32-filterable');
        } else {
            console.warn("Device does not support 'float32-filterable'. Some effects may not work as intended.");
        }

        this.device = await adapter.requestDevice({
            requiredFeatures,
        });
        // --- End of fix ---

        this.context = this.canvas.getContext('webgpu')!;
        this.presentationFormat = navigator.gpu.getPreferredCanvasFormat();
        this.context.configure({ device: this.device, format: this.presentationFormat, alphaMode: 'premultiplied' });

        await this.fetchImageUrls();
        await this.createResources();
        await this.createPipelines();

        return true;
    }

    private async fetchImageUrls(): Promise<void> {
        const bucketName = 'my-sd35-space-images-2025';
        const apiUrl = `https://storage.googleapis.com/storage/v1/b/${bucketName}/o`;
        try {
            const response = await fetch(apiUrl);
            if (!response.ok) throw new Error(`API error: ${response.status}`);
            const data = await response.json();
            this.imageUrls = data.items ? data.items.map((item: { name: string }) => `https://storage.googleapis.com/${bucketName}/${item.name}`) : [];
        } catch (e) {
            console.error("Failed to fetch image list:", e);
            this.imageUrls = ['https://i.imgur.com/vCNL2sT.jpeg'];
        }
    }

    public async loadRandomImage(): Promise<string | undefined> {
        try {
            if (this.imageUrls.length === 0) return;
            const imageUrl = this.imageUrls[Math.floor(Math.random() * this.imageUrls.length)];
            const response = await fetch(imageUrl);
            const imageBitmap = await createImageBitmap(await response.blob());
            this.imageDimensions = { width: imageBitmap.width, height: imageBitmap.height };

            if (this.imageTexture) this.imageTexture.destroy();
            this.imageTexture = this.device.createTexture({
                size: [imageBitmap.width, imageBitmap.height],
                format: 'rgba16float',
                usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC,
            });
            this.device.queue.copyExternalImageToTexture({ source: imageBitmap }, { texture: this.imageTexture }, [imageBitmap.width, imageBitmap.height]);

            this.createBindGroups();
            return imageUrl;
        } catch (e) {
            console.error("Failed to load image:", e);
            return undefined;
        }
    }

  public handleResize(): void {
    if (!this.device || !this.canvas.parentElement) return;

    // Get the current size of the container
    const containerWidth = this.canvas.parentElement.clientWidth;
    const containerHeight = this.canvas.parentElement.clientHeight;
    
    const imageAspect = this.imageDimensions.width / this.imageDimensions.height;
    
    let newCanvasWidth = containerWidth;
    let newCanvasHeight = Math.round(containerWidth / imageAspect);

    if (newCanvasHeight > containerHeight) {
        newCanvasHeight = containerHeight;
        newCanvasWidth = Math.round(containerHeight * imageAspect);
    }
    
    // Only resize if there's a meaningful change to avoid unnecessary re-creations
    if (this.canvas.width !== newCanvasWidth || this.canvas.height !== newCanvasHeight) {
        this.canvas.style.width = newCanvasWidth + 'px';
        this.canvas.style.height = newCanvasHeight + 'px';
        this.canvas.width = newCanvasWidth;
        this.canvas.height = newCanvasHeight;

        this.context.configure({device: this.device, format: this.presentationFormat, alphaMode: 'premultiplied'});

        if (this.writeTexture) this.writeTexture.destroy();
        this.writeTexture = this.device.createTexture({
            size: [newCanvasWidth, newCanvasHeight],
            format: 'rgba16float',
            usage: GPUTextureUsage.COPY_DST | GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
        });
        this.createBindGroups();
    }
}
    
    public updateDepthMap(data: Float32Array, width: number, height: number): void {
        if (!this.device) return;
        if (this.staticDepthTexture && (this.staticDepthTexture.width !== width || this.staticDepthTexture.height !== height)) {
            this.staticDepthTexture.destroy();
        }

        if (!this.staticDepthTexture || this.staticDepthTexture.width !== width || this.staticDepthTexture.height !== height) {
            const depthTextureDescriptor: GPUTextureDescriptor = {
                size: [width, height],
                format: 'r32float',
                usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
            };
            this.staticDepthTexture = this.device.createTexture(depthTextureDescriptor);
        }

        this.device.queue.writeTexture({ texture: this.staticDepthTexture }, data, { bytesPerRow: width * 4, rowsPerImage: height }, [width, height]);
        this.createBindGroups();
    }

    private async createResources(): Promise<void> {
        this.sampler = this.device.createSampler({ magFilter: 'linear', minFilter: 'linear' });
        this.nonFilteringSampler = this.device.createSampler({ magFilter: 'nearest', minFilter: 'nearest' });
  this.v2ComputeUniformBuffer = this.device.createBuffer({
      size: 96, // 6 vec4s * 16 bytes/vec4 = 96 bytes
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST 
  });
        const placeholderDepthDescriptor: GPUTextureDescriptor = {
            size: [1, 1],
            format: 'r32float',
            usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
        };
        this.staticDepthTexture = this.device.createTexture(placeholderDepthDescriptor);
        this.device.queue.writeTexture({ texture: this.staticDepthTexture }, new Float32Array([0.0]), { bytesPerRow: 4 }, [1, 1]);

        this.writeTexture = this.device.createTexture({
            size: [this.canvas.width, this.canvas.height],
            format: 'rgba16float',
            usage: GPUTextureUsage.COPY_DST | GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
        });
        await this.loadRandomImage();
    }

    private async createPipelines(): Promise<void> {
        const [zoomCode, textureCode] = await Promise.all([
            fetch('shaders/3d-zoom.wgsl').then(res => res.text()),
            fetch('shaders/texture.wgsl').then(res => res.text()),
        ]);
        const zoomModule = this.device.createShaderModule({ code: zoomCode });
        const textureModule = this.device.createShaderModule({ code: textureCode });
        const commonConfig = { vertex: { module: textureModule, entryPoint: 'vs_main' }, fragment: { targets: [{ format: this.presentationFormat }] }, primitive: { topology: 'triangle-strip' as GPUPrimitiveTopology } };
        this.pipelines.set('present', this.device.createRenderPipeline({ layout: 'auto', ...commonConfig, fragment: { ...commonConfig.fragment, module: textureModule, entryPoint: 'fs_main' } }));
        this.pipelines.set('computeZoom', this.device.createComputePipeline({ layout: 'auto', compute: { module: zoomModule, entryPoint: 'main' } }));
    }

    private createBindGroups(): void {
        if (!this.imageTexture || !this.staticDepthTexture) return;

        this.bindGroups.set('present', this.device.createBindGroup({ layout: this.pipelines.get('present')!.getBindGroupLayout(0), entries: [{ binding: 0, resource: this.sampler }, { binding: 1, resource: this.writeTexture.createView() }] }));

        const computeZoomPipeline = this.pipelines.get('computeZoom');
        if (computeZoomPipeline) {
            this.bindGroups.set('computeZoom', this.device.createBindGroup({
                layout: computeZoomPipeline.getBindGroupLayout(0),
                entries: [
                    { binding: 0, resource: this.sampler },
                    { binding: 1, resource: this.imageTexture.createView() },
                    { binding: 2, resource: this.writeTexture.createView() },
                    { binding: 3, resource: { buffer: this.v2ComputeUniformBuffer } },
                    { binding: 4, resource: this.nonFilteringSampler },
                    { binding: 5, resource: this.staticDepthTexture.createView() },
                ]
            }));
        }
    }

    public getImageDimensions(): { width: number, height: number } {
        return this.imageDimensions;
    }

public render(mode: RenderMode, farthestPoint: { x: number, y: number }, depthThreshold: number, edgeHardness: number, imageDimensions: {width: number, height: number}, depthLevels: number, depthDimensions: {width: number, height: number}): void {
    if (!this.device || !this.imageTexture) return;
    const currentTime = performance.now() / 1000.0;
    const commandEncoder = this.device.createCommandEncoder();

    if (mode === '3d-zoom') {
        const computePass = commandEncoder.beginComputePass();
        const computeZoomBG = this.bindGroups.get('computeZoom');
        if (computeZoomBG) {
            const uniformArray = new Float32Array(12);

            // vec4 0: Resolutions (canvas.xy, image.zw)
            uniformArray.set([this.canvas.width, this.canvas.height, imageDimensions.width, imageDimensions.height], 0);
            
            // vec4 1: Time, Zoom Center, and a new slot for depth map resolution
            uniformArray.set([currentTime, farthestPoint.x, farthestPoint.y], 4);
            
            // vec4 2: Config values and the new depth map resolution
            uniformArray.set([depthThreshold, edgeHardness, depthLevels], 8);
            
            // Overwrite specific slots for the dedicated depth_map_res uniform
            // This is a bit of a hack to fit it in the existing buffer structure
            // Let's send depth_res in a cleaner way.
            
            // --- New, Cleaner Uniform Layout ---
           const cleanUniforms = new Float32Array(24); 
  
// vec4 0 (Offset 0): resolutions
cleanUniforms.set([this.canvas.width, this.canvas.height], 0); // Canvas res
// We can use imageDimensions from the render args or this.imageDimensions
cleanUniforms.set([imageDimensions.width, imageDimensions.height], 2); // Original color texture res

// vec4 1 (Offset 4): time_zoom
cleanUniforms.set([currentTime, farthestPoint.x, farthestPoint.y], 4);

// vec4 2 (Offset 8): config (fog_color.xyz, fog_density.w)
// Example fog values: dark gray fog with a density of 4.0
const fogColor = [0.1, 0.1, 0.12];
const fogDensity = 4.0;
cleanUniforms.set([...fogColor, fogDensity], 8);

// vec4 3 (Offset 12): depth_map_res
cleanUniforms.set([depthDimensions.width, depthDimensions.height], 12);

// vec4 4 (Offset 16): color_map_res - THIS IS THE NEWLY REQUIRED DATA
cleanUniforms.set([imageDimensions.width, imageDimensions.height], 16);

// vec4 5 (Offset 20): effect_params (parallax_strength.x) - ALSO NEW
const parallaxStrength = 0.05; // A good starting value for parallax
cleanUniforms.set([parallaxStrength], 20);


this.device.queue.writeBuffer(this.v2ComputeUniformBuffer, 0, cleanUniforms);



            computePass.setPipeline(this.pipelines.get('computeZoom') as GPUComputePipeline);
            computePass.setBindGroup(0, computeZoomBG);
            computePass.dispatchWorkgroups(this.canvas.width / 8, this.canvas.height / 8, 1);
        }
        computePass.end();
        }

        const textureView = this.context.getCurrentTexture().createView();
        const renderPassDescriptor: GPURenderPassDescriptor = {
            colorAttachments: [{
                view: textureView,
                clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 },
                loadOp: 'clear' as GPULoadOp,
                storeOp: 'store' as GPUStoreOp
            }]
        };
        const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
        const presentPipeline = this.pipelines.get('present') as GPURenderPipeline;
        if (presentPipeline && this.bindGroups.has('present')) {
            passEncoder.setPipeline(presentPipeline);
            passEncoder.setBindGroup(0, this.bindGroups.get('present')!);
            passEncoder.draw(4);
        }
        passEncoder.end();
        this.device.queue.submit([commandEncoder.finish()]);
    }
}
